import React, { useState, useCallback } from "react";

import { Product } from "../types/catalog";
import { Cart, CartItem } from "../types/cart";
import { ProductModalProduct } from "../types/product_modal";
import { useStorefrontConfig, useStorefrontHelper } from "../types/storefront";
import { ProductModalCard } from "./product_modal_card";
import { classNames } from "src/common_util";

class ProductModalStackItem {
  readonly productModalProduct: ProductModalProduct;
  readonly label?: string;
  readonly onConfirm: (productModalProduct: ProductModalProduct) => Promise<void>;
  readonly onCancel: (reason: string) => Promise<void>;
  readonly remainingQuantityAllowed?: number;

  constructor(
    productModalProduct: ProductModalProduct,
    onConfirm: (productModalProduct: ProductModalProduct) => Promise<void>,
    onCancel: (reason: string) => Promise<void>,
    label?: string,
    remainingQuantityAllowed?: number,
  ) {
    this.productModalProduct = productModalProduct;
    this.onConfirm = onConfirm;
    this.onCancel = onCancel;
    this.label = label;
    this.remainingQuantityAllowed = remainingQuantityAllowed;
  }
}

const ProductModal = ({
  product,
  existingCartItem,
  hideModal,
  addItemToCart,
  editItemInCart,
  modalRef,
  active,
}: {
  product: Product;
  existingCartItem?: CartItem;
  hideModal: (updateHistory: boolean) => void;
  addItemToCart: (cartItem: CartItem) => Promise<Cart>;
  editItemInCart: (cartItem: CartItem) => Promise<Cart>;
  modalRef: React.MutableRefObject<HTMLDivElement | null>;
  active: boolean;
}): React.JSX.Element => {
  const config = useStorefrontConfig();
  const { isOrderable, isInFulfillableTimeRange } = useStorefrontHelper();

  const closeModalViaCurtain = (e: React.MouseEvent) => {
    if (e.currentTarget != e.target) { return; }
    hideModal(true);
  };

  const closeModal = useCallback(() => {
    hideModal(true);
  }, [hideModal]);

  const onAddItemToCart = async (productModalProduct: ProductModalProduct) => {
    await addItemToCart(productModalProduct.toCartItem());
  };

  const onEditItemInCart = async (productModalProduct: ProductModalProduct) => {
    await editItemInCart(productModalProduct.toCartItem(existingCartItem?.idTarget));
  };

  const productModalProduct = existingCartItem ? ProductModalProduct.fromCartItem(product, existingCartItem) : ProductModalProduct.fromProduct(product);

  const initialStackItem = new ProductModalStackItem(
    productModalProduct,
    existingCartItem ? onEditItemInCart : onAddItemToCart,
    async () => { /* Do nothing */ },
  );

  const [productStack, setProductStack] = useState([initialStackItem]);

  const addCustomisationModal = useCallback((
    productModalProduct: ProductModalProduct,
    label: string,
    onConfirm: (productModalProduct: ProductModalProduct) => Promise<void>,
    onCancel: (reason: string) => Promise<void>,
    remainingQuantityAllowed?: number,
  ) : void => {
    const stackItem = new ProductModalStackItem(
      productModalProduct,
      onConfirm,
      onCancel,
      label,
      remainingQuantityAllowed,
    );

    setProductStack(productStack.concat([stackItem]));
  }, [productStack, setProductStack]);

  const handlePopResolve = useCallback(async (productModalProduct: ProductModalProduct) => {
    const stackItem = productStack[productStack.length - 1];
    await stackItem?.onConfirm(productModalProduct);
    setProductStack(productStack.slice(0, productStack.length - 1));

    if (productStack.length <= 1) {
      closeModal();
    }
  }, [productStack, setProductStack, closeModal]);

  const handlePopReject = useCallback(async () => {
    const stackItem = productStack[productStack.length - 1];
    setProductStack(productStack.slice(0, productStack.length - 1));
    stackItem?.onCancel("User didn't make a selection");

    if (productStack.length <= 1) {
      closeModal();
    }
  }, [productStack, setProductStack, closeModal]);

  const classes = classNames({
    "modal": true,
    "is-active": active,
  });

  const isDisabled = config.presentationStatus != 'open_for_business' || !isOrderable(product) || !isInFulfillableTimeRange(product);

  const productModalCards = productStack.map((stackItem, idx) => <ProductModalCard
      key={stackItem.productModalProduct.product.keyForUrl()}
      productModalProduct={stackItem.productModalProduct}
      label={stackItem.label}
      confirmProductSelections={handlePopResolve}
      modalBack={handlePopReject}
      closeModal={closeModal}
      addCustomisationModal={addCustomisationModal}
      isVisible={productStack.length - 1 === idx}
      isEdit={!!existingCartItem}
      isNested={idx > 0}
      remainingQuantityAllowed={stackItem.remainingQuantityAllowed}
      disabledButtons={isDisabled}
    />
  );

  return (
    <div className={classes} ref={modalRef} onClick={closeModalViaCurtain} data-testid={"product-modal"} >
      {productModalCards}
    </div>
  );
}

export { ProductModal };
